// Catálogo del hotel: Tipos de habitación, Habitaciones y Planes de tarifa.
// Hotel → Catálogo. Toda escritura exige nivel de acceso 5 (hotel._exigir_admin
// en tipo_guardar/habitacion_guardar/plan_guardar); sin ese nivel la pantalla
// queda en modo solo-lectura (se deshabilitan los botones de alta/edición, el
// servidor igual lo rechazaría).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BedDouble, ImagePlus, Layers, ListChecks, Pencil, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { PartnerOpt, partnersList, resizeToDataURL } from '@/features/inventario/shared';
import {
  Campo, Modal, Switch, fieldCls, money, num, useSort, SortTh,
  hotelRpc, hotelTabla, QK,
  Habitacion, Plan, TipoHabitacion,
} from './shared';

type Tab = 'tipos' | 'habitaciones' | 'planes';

export function CatalogoHotel() {
  const [tab, setTab] = useState<Tab>('tipos');
  const { isAdmin } = usePermissions();
  const puedeEditar = isAdmin();

  const tabBtn = (k: Tab, label: string) => (
    <button type="button" onClick={() => setTab(k)}
      className={`rounded px-3 py-1 text-sm ${tab === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
      {label}
    </button>
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Layers className="w-5 h-5" /> Catálogo del hotel</h1>
        <div className="flex rounded-md border p-0.5">
          {tabBtn('tipos', 'Tipos de habitación')}
          {tabBtn('habitaciones', 'Habitaciones')}
          {tabBtn('planes', 'Planes de tarifa')}
        </div>
      </div>
      {!puedeEditar && (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
          Solo un administrador (nivel de acceso 5) puede crear o editar el catálogo. Aquí puedes consultarlo.
        </p>
      )}
      {tab === 'tipos' && <TiposTab puedeEditar={puedeEditar} />}
      {tab === 'habitaciones' && <HabitacionesTab puedeEditar={puedeEditar} />}
      {tab === 'planes' && <PlanesTab puedeEditar={puedeEditar} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tipos de habitación
// ---------------------------------------------------------------------------
const tiposList = () => hotelTabla<TipoHabitacion>('tipos_habitacion', 'order=orden,codigo');

function TiposTab({ puedeEditar }: { puedeEditar: boolean }) {
  const qc = useQueryClient();
  const { data: tipos = [], isLoading } = useQuery<TipoHabitacion[]>({ queryKey: [QK, 'tipos'], queryFn: tiposList });
  const sort = useSort<TipoHabitacion>(tipos, (r, k) => {
    switch (k) {
      case 'codigo': return r.codigo;
      case 'nombre': return r.nombre;
      case 'capacidad': return r.capacidad_adultos * 100 + r.capacidad_ninos;
      case 'tarifa': return r.tarifa_base;
      case 'orden': return r.orden;
      case 'estado': return r.es_activo ? 1 : 0;
      default: return null;
    }
  });
  const [modal, setModal] = useState<TipoHabitacion | 'nuevo' | null>(null);
  const totalTarifa = sort.sorted.reduce((s, t) => s + Number(t.tarifa_base || 0), 0);

  return (
    <Card>
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-base">Tipos de habitación ({tipos.length})</CardTitle>
        {puedeEditar && <Button size="sm" onClick={() => setModal('nuevo')}><Plus className="w-4 h-4 mr-1" /> Nuevo tipo</Button>}
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {isLoading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div> : (
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th>
              <SortTh col="codigo" sort={sort}>Código</SortTh>
              <SortTh col="nombre" sort={sort}>Nombre</SortTh>
              <SortTh col="capacidad" sort={sort} align="right">Capacidad</SortTh>
              <th className="py-2 pr-3">Camas</th>
              <SortTh col="tarifa" sort={sort} align="right">Tarifa base</SortTh>
              <th className="py-2 pr-3">Imagen</th>
              <SortTh col="orden" sort={sort} align="right">Orden</SortTh>
              <SortTh col="estado" sort={sort}>Estado</SortTh>
              <th className="py-2"></th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin tipos de habitación</td></tr>}
              {sort.sorted.map((t, i) => (
                <tr key={t.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{t.codigo}</td>
                  <td className="py-2 pr-3">{t.nombre}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{t.capacidad_adultos} ad.{t.capacidad_ninos > 0 ? ` + ${t.capacidad_ninos} niño(s)` : ''}
                    {(Number(t.extra_adulto ?? 0) > 0 || Number(t.extra_nino ?? 0) > 0) && <div className="text-[11px] text-muted-foreground">incluye {t.ocupacion_base ?? 2} · +{money(Number(t.extra_adulto ?? 0))} ad. · +{money(Number(t.extra_nino ?? 0))} niño</div>}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{t.camas ?? '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{money(t.tarifa_base)}</td>
                  <td className="py-2 pr-3">{t.imagen ? <img src={t.imagen} alt={t.nombre} className="h-9 w-9 rounded object-cover border" /> : <span className="text-xs text-muted-foreground">—</span>}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{t.orden}</td>
                  <td className="py-2 pr-3">{t.es_activo ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">Activo</span> : <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">Inactivo</span>}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {puedeEditar && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setModal(t)}><Pencil className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {sort.sorted.length > 0 && (
              <tfoot><tr className="border-t font-semibold">
                <td className="py-2 pr-3" colSpan={5}>Total</td>
                <td className="py-2 pr-3 text-right tabular-nums">{money(totalTarifa)}</td>
                <td colSpan={4}></td>
              </tr></tfoot>
            )}
          </table>
        )}
      </CardContent>
      {modal && (
        <TipoModal tipo={modal === 'nuevo' ? null : modal} onClose={() => setModal(null)}
          onSaved={() => { setModal(null); void qc.invalidateQueries({ queryKey: [QK] }); }} />
      )}
    </Card>
  );
}

function TipoModal({ tipo, onClose, onSaved }: { tipo: TipoHabitacion | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    codigo: tipo?.codigo ?? '', nombre: tipo?.nombre ?? '', descripcion: tipo?.descripcion ?? '',
    capacidad_adultos: tipo?.capacidad_adultos ?? 2, capacidad_ninos: tipo?.capacidad_ninos ?? 0,
    camas: tipo?.camas ?? '', tarifa_base: tipo?.tarifa_base ?? 0, imagen: tipo?.imagen ?? '',
    orden: tipo?.orden ?? 0, es_activo: tipo?.es_activo ?? true,
    ocupacion_base: tipo?.ocupacion_base ?? 2, extra_adulto: tipo?.extra_adulto ?? 0,
    extra_nino: tipo?.extra_nino ?? 0, ninos_sin_recargo: tipo?.ninos_sin_recargo ?? 0,
  });
  const [subiendo, setSubiendo] = useState(false);

  const guardar = useMutation({
    mutationFn: () => hotelRpc('tipo_guardar', {
      _d: {
        id: tipo?.id, codigo: form.codigo.trim(), nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim() || null, capacidad_adultos: form.capacidad_adultos,
        capacidad_ninos: form.capacidad_ninos, camas: form.camas.trim() || null,
        tarifa_base: form.tarifa_base, imagen: form.imagen || null, orden: form.orden, es_activo: form.es_activo,
        ocupacion_base: form.ocupacion_base, extra_adulto: form.extra_adulto, extra_nino: form.extra_nino, ninos_sin_recargo: form.ninos_sin_recargo,
      },
    }),
    onSuccess: () => { toast.success('Tipo de habitación guardado'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const url = await resizeToDataURL(file, 480, 0.75);
      setForm((f) => ({ ...f, imagen: url }));
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setSubiendo(false); }
  };

  return (
    <Modal title={tipo ? `Editar ${tipo.codigo}` : 'Nuevo tipo de habitación'} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Código *"><input className={fieldCls} value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })} /></Campo>
          <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
        </div>
        <Campo label="Descripción"><textarea className={`${fieldCls} h-16`} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></Campo>
        <div className="grid grid-cols-3 gap-3">
          <Campo label="Adultos"><input type="number" min={1} className={fieldCls} value={form.capacidad_adultos} onChange={(e) => setForm({ ...form, capacidad_adultos: Math.max(1, Number(e.target.value) || 1) })} /></Campo>
          <Campo label="Niños"><input type="number" min={0} className={fieldCls} value={form.capacidad_ninos} onChange={(e) => setForm({ ...form, capacidad_ninos: Math.max(0, Number(e.target.value) || 0) })} /></Campo>
          <Campo label="Camas"><input className={fieldCls} placeholder="1 matrimonial" value={form.camas} onChange={(e) => setForm({ ...form, camas: e.target.value })} /></Campo>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Tarifa base *"><input type="number" min={0} step="0.01" className={fieldCls} value={form.tarifa_base} onChange={(e) => setForm({ ...form, tarifa_base: Number(e.target.value) || 0 })} /></Campo>
          <Campo label="Orden"><input type="number" className={fieldCls} value={form.orden} onChange={(e) => setForm({ ...form, orden: Number(e.target.value) || 0 })} /></Campo>
        </div>
        <div className="rounded-md border p-3">
          <p className="mb-2 text-xs font-medium text-muted-foreground">Precio por ocupación (recargos por noche sobre la tarifa)</p>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Campo label="Personas incluidas"><input type="number" min={1} className={fieldCls} value={form.ocupacion_base} onChange={(e) => setForm({ ...form, ocupacion_base: Math.max(1, Number(e.target.value) || 1) })} /></Campo>
            <Campo label="Adulto extra"><input type="number" min={0} step="0.01" className={fieldCls} value={form.extra_adulto} onChange={(e) => setForm({ ...form, extra_adulto: Number(e.target.value) || 0 })} /></Campo>
            <Campo label="Niño extra"><input type="number" min={0} step="0.01" className={fieldCls} value={form.extra_nino} onChange={(e) => setForm({ ...form, extra_nino: Number(e.target.value) || 0 })} /></Campo>
            <Campo label="Niños sin recargo"><input type="number" min={0} className={fieldCls} value={form.ninos_sin_recargo} onChange={(e) => setForm({ ...form, ninos_sin_recargo: Math.max(0, Number(e.target.value) || 0) })} /></Campo>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Ej.: tarifa {money(form.tarifa_base)} para {form.ocupacion_base} persona(s); con {form.ocupacion_base + 1} adultos = {money(Number(form.tarifa_base) + Number(form.extra_adulto))} por noche.
          </p>
        </div>
        <Campo label="Imagen (opcional)">
          <div className="flex items-center gap-3">
            {form.imagen ? (
              <div className="relative">
                <img src={form.imagen} alt="" className="h-16 w-16 rounded object-cover border" />
                <button type="button" onClick={() => setForm((f) => ({ ...f, imagen: '' }))}
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-background border p-0.5 hover:bg-muted" title="Quitar imagen">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded border border-dashed text-muted-foreground hover:bg-muted">
                <ImagePlus className="h-5 w-5" />
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
              </label>
            )}
            {subiendo && <span className="text-xs text-muted-foreground">Procesando imagen…</span>}
          </div>
        </Campo>
        <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} disabled={!form.codigo.trim() || !form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Habitaciones
// ---------------------------------------------------------------------------
const habitacionesList = () => hotelTabla<Habitacion>('habitaciones', 'order=orden,numero');

function HabitacionesTab({ puedeEditar }: { puedeEditar: boolean }) {
  const qc = useQueryClient();
  const { data: habs = [], isLoading } = useQuery<Habitacion[]>({ queryKey: [QK, 'habitaciones'], queryFn: habitacionesList });
  const { data: tipos = [] } = useQuery<TipoHabitacion[]>({ queryKey: [QK, 'tipos'], queryFn: tiposList });
  const tipoNombre = (id: string) => { const t = tipos.find((x) => x.id === id); return t ? `${t.codigo} · ${t.nombre}` : '—'; };
  const sort = useSort<Habitacion>(habs, (r, k) => {
    switch (k) {
      case 'numero': return r.numero;
      case 'tipo': return tipoNombre(r.tipo_id);
      case 'piso': return r.piso;
      case 'limpieza': return r.estado_limpieza;
      case 'mant': return r.estado_mant;
      case 'orden': return r.orden;
      case 'estado': return r.es_activo ? 1 : 0;
      default: return null;
    }
  });
  const [modal, setModal] = useState<Habitacion | 'nuevo' | null>(null);
  const [masiva, setMasiva] = useState(false);

  return (
    <Card>
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-base">Habitaciones ({habs.length})</CardTitle>
        {puedeEditar && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setMasiva(true)}><ListChecks className="w-4 h-4 mr-1" /> Alta masiva</Button>
            <Button size="sm" onClick={() => setModal('nuevo')}><Plus className="w-4 h-4 mr-1" /> Nueva</Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {isLoading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div> : (
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th>
              <SortTh col="numero" sort={sort}>Número</SortTh>
              <SortTh col="tipo" sort={sort}>Tipo</SortTh>
              <SortTh col="piso" sort={sort}>Piso</SortTh>
              <SortTh col="limpieza" sort={sort}>Limpieza</SortTh>
              <SortTh col="mant" sort={sort}>Mantenimiento</SortTh>
              <th className="py-2 pr-3">Notas</th>
              <SortTh col="orden" sort={sort} align="right">Orden</SortTh>
              <SortTh col="estado" sort={sort}>Estado</SortTh>
              <th className="py-2"></th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin habitaciones</td></tr>}
              {sort.sorted.map((h, i) => (
                <tr key={h.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-mono">{h.numero}</td>
                  <td className="py-2 pr-3">{tipoNombre(h.tipo_id)}</td>
                  <td className="py-2 pr-3">{h.piso ?? '—'}</td>
                  <td className="py-2 pr-3 text-xs capitalize">{h.estado_limpieza}</td>
                  <td className="py-2 pr-3 text-xs">{h.estado_mant === 'ok' ? 'Operativa' : h.estado_mant.replace('_', ' ')}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground" title={h.notas ?? ''}>{h.notas ? (h.notas.length > 24 ? `${h.notas.slice(0, 24)}…` : h.notas) : '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{h.orden}</td>
                  <td className="py-2 pr-3">{h.es_activo ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">Activa</span> : <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">Inactiva</span>}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {puedeEditar && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setModal(h)}><Pencil className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
      {modal && (
        <HabitacionModal habitacion={modal === 'nuevo' ? null : modal} tipos={tipos} onClose={() => setModal(null)}
          onSaved={() => { setModal(null); void qc.invalidateQueries({ queryKey: [QK] }); }} />
      )}
      {masiva && (
        <AltaMasivaModal tipos={tipos} onClose={() => setMasiva(false)}
          onDone={() => void qc.invalidateQueries({ queryKey: [QK] })} />
      )}
    </Card>
  );
}

function HabitacionModal({ habitacion, tipos, onClose, onSaved }: { habitacion: Habitacion | null; tipos: TipoHabitacion[]; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    numero: habitacion?.numero ?? '', tipo_id: habitacion?.tipo_id ?? (tipos[0]?.id ?? ''),
    piso: habitacion?.piso ?? '', notas: habitacion?.notas ?? '',
    orden: habitacion?.orden ?? 0, es_activo: habitacion?.es_activo ?? true,
  });
  const guardar = useMutation({
    mutationFn: () => hotelRpc('habitacion_guardar', {
      _d: {
        id: habitacion?.id, numero: form.numero.trim(), tipo_id: form.tipo_id || null,
        piso: form.piso.trim() || null, notas: form.notas.trim() || null, orden: form.orden, es_activo: form.es_activo,
      },
    }),
    onSuccess: () => { toast.success('Habitación guardada'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Modal title={habitacion ? `Editar habitación ${habitacion.numero}` : 'Nueva habitación'} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Número *"><input className={fieldCls} value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} /></Campo>
          <Campo label="Piso"><input className={fieldCls} value={form.piso} onChange={(e) => setForm({ ...form, piso: e.target.value })} /></Campo>
        </div>
        <Campo label="Tipo de habitación *">
          <select className={fieldCls} value={form.tipo_id} onChange={(e) => setForm({ ...form, tipo_id: e.target.value })}>
            <option value="">— Elegir —</option>
            {tipos.map((t) => <option key={t.id} value={t.id}>{t.codigo} · {t.nombre}</option>)}
          </select>
        </Campo>
        <Campo label="Notas"><textarea className={`${fieldCls} h-16`} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} /></Campo>
        <Campo label="Orden"><input type="number" className={`${fieldCls} w-32`} value={form.orden} onChange={(e) => setForm({ ...form, orden: Number(e.target.value) || 0 })} /></Campo>
        <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activa" />
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} disabled={!form.numero.trim() || !form.tipo_id} onClick={() => guardar.mutate()}>Guardar</Button>
        </div>
      </div>
    </Modal>
  );
}

function AltaMasivaModal({ tipos, onClose, onDone }: { tipos: TipoHabitacion[]; onClose: () => void; onDone: () => void }) {
  const [desde, setDesde] = useState('101');
  const [hasta, setHasta] = useState('110');
  const [tipoId, setTipoId] = useState(tipos[0]?.id ?? '');
  const [piso, setPiso] = useState('');
  const [procesando, setProcesando] = useState(false);

  const crear = async () => {
    if (!tipoId) { toast.error('Elija el tipo de habitación'); return; }
    const n1 = parseInt(desde.trim(), 10);
    const n2 = parseInt(hasta.trim(), 10);
    if (!Number.isFinite(n1) || !Number.isFinite(n2) || n2 < n1) { toast.error('Rango inválido'); return; }
    if (n2 - n1 + 1 > 200) { toast.error('Máximo 200 habitaciones por lote'); return; }
    const width = Math.max(desde.trim().length, hasta.trim().length);
    setProcesando(true);
    let ok = 0; let fail = 0; let primerError = '';
    for (let n = n1; n <= n2; n++) {
      const numero = String(n).padStart(width, '0');
      try {
        await hotelRpc('habitacion_guardar', { _d: { numero, tipo_id: tipoId, piso: piso.trim() || null } });
        ok++;
      } catch (e) { fail++; if (!primerError) primerError = `${numero}: ${getErrorMessage(e)}`; }
    }
    setProcesando(false);
    if (ok) toast.success(`${ok} habitación(es) creada(s)`);
    if (fail) toast.error(`${fail} no se pudieron crear — ${primerError}`);
    onDone();
    if (!fail) onClose();
  };

  return (
    <Modal title="Crear habitaciones en lote" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">Crea un rango consecutivo (p. ej. de la 101 a la 110), todas del mismo tipo y piso.</p>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Desde *"><input className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label="Hasta *"><input className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
        </div>
        <Campo label="Tipo de habitación *">
          <select className={fieldCls} value={tipoId} onChange={(e) => setTipoId(e.target.value)}>
            <option value="">— Elegir —</option>
            {tipos.map((t) => <option key={t.id} value={t.id}>{t.codigo} · {t.nombre}</option>)}
          </select>
        </Campo>
        <Campo label="Piso (opcional, igual para todas)"><input className={fieldCls} value={piso} onChange={(e) => setPiso(e.target.value)} /></Campo>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose} disabled={procesando}>Cancelar</Button>
          <Button isLoading={procesando} onClick={crear}><BedDouble className="w-4 h-4 mr-1" /> Crear</Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Planes de tarifa
// ---------------------------------------------------------------------------
function PlanesTab({ puedeEditar }: { puedeEditar: boolean }) {
  const qc = useQueryClient();
  const { data: planes = [], isLoading } = useQuery<Plan[]>({
    queryKey: [QK, 'planes'], queryFn: () => hotelTabla<Plan>('planes', 'order=orden,codigo'),
  });
  const { data: partners = [] } = useQuery<PartnerOpt[]>({ queryKey: [QK, 'partners'], queryFn: partnersList });
  const { data: comps = [] } = useQuery<Componente[]>({ queryKey: [QK, 'plan_componentes'], queryFn: () => hotelTabla<Componente>('plan_componentes', 'es_activo=eq.true&order=orden') });
  const partnerNombre = (id: string | null) => (id ? (partners.find((p) => p.id === id)?.razon_social ?? '—') : null);
  const sort = useSort<Plan>(planes, (r, k) => {
    switch (k) {
      case 'codigo': return r.codigo;
      case 'nombre': return r.nombre;
      case 'ajuste': return r.ajuste_valor;
      case 'orden': return r.orden;
      case 'estado': return r.es_activo ? 1 : 0;
      default: return null;
    }
  });
  const [modal, setModal] = useState<Plan | 'nuevo' | null>(null);
  const ajusteTxt = (p: Plan) => {
    const signo = p.ajuste_valor >= 0 ? '+' : '';
    return p.ajuste_tipo === 'pct' ? `${signo}${num(p.ajuste_valor, 1)}%` : `${signo}${money(p.ajuste_valor)}`;
  };

  return (
    <Card>
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-base">Planes de tarifa ({planes.length})</CardTitle>
        {puedeEditar && <Button size="sm" onClick={() => setModal('nuevo')}><Plus className="w-4 h-4 mr-1" /> Nuevo plan</Button>}
      </CardHeader>
      <CardContent className="space-y-3 overflow-x-auto">
        <p className="text-xs text-muted-foreground">
          El ajuste se aplica sobre la <strong>tarifa de la habitación</strong> (por ejemplo, −10&nbsp;% o −50&nbsp;% en la promo 2x1).
          Lo que el plan <strong>incluye</strong> (desayuno, cenas, tours, masajes…) se define como componentes: se cobran como líneas propias, con su IVA y su ingreso (A&amp;B, spa, tours).
          Los campos de depósito, cancelación y no-show que se dejen vacíos <strong>heredan</strong> la configuración general del hotel.
        </p>
        {isLoading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div> : (
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th>
              <SortTh col="codigo" sort={sort}>Código</SortTh>
              <SortTh col="nombre" sort={sort}>Nombre</SortTh>
              <SortTh col="ajuste" sort={sort} align="right">Ajuste</SortTh>
              <th className="py-2 pr-3">Incluye</th>
              <th className="py-2 pr-3">Reembolsable</th>
              <th className="py-2 pr-3">Empresa/cliente</th>
              <th className="py-2 pr-3">Público</th>
              <SortTh col="orden" sort={sort} align="right">Orden</SortTh>
              <SortTh col="estado" sort={sort}>Estado</SortTh>
              <th className="py-2"></th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-muted-foreground">Sin planes de tarifa</td></tr>}
              {sort.sorted.map((p, i) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{p.codigo}</td>
                  <td className="py-2 pr-3">{p.nombre}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{ajusteTxt(p)}</td>
                  <td className="py-2 pr-3 text-xs">{comps.filter((c) => c.plan_id === p.id).map((c) => c.nombre).join(' · ') || (p.incluye_desayuno ? 'Desayuno' : '—')}</td>
                  <td className="py-2 pr-3">{p.reembolsable ? '✔' : '—'}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{partnerNombre(p.partner_id) ?? '—'}</td>
                  <td className="py-2 pr-3">{p.es_publico ? '✔' : '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{p.orden}</td>
                  <td className="py-2 pr-3">{p.es_activo ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">Activo</span> : <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">Inactivo</span>}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {puedeEditar && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setModal(p)}><Pencil className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
      {modal && (
        <PlanModal plan={modal === 'nuevo' ? null : modal} partners={partners} onClose={() => setModal(null)}
          onSaved={() => { setModal(null); void qc.invalidateQueries({ queryKey: [QK] }); }} />
      )}
    </Card>
  );
}

function PlanModal({ plan, partners, onClose, onSaved }: { plan: Plan | null; partners: PartnerOpt[]; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    codigo: plan?.codigo ?? '', nombre: plan?.nombre ?? '',
    ajuste_tipo: plan?.ajuste_tipo ?? 'pct' as 'pct' | 'monto', ajuste_valor: plan?.ajuste_valor ?? 0,
    incluye_desayuno: plan?.incluye_desayuno ?? false, reembolsable: plan?.reembolsable ?? true,
    deposito_pct: plan?.deposito_pct != null ? String(plan.deposito_pct) : '',
    cancel_dias: plan?.cancel_dias != null ? String(plan.cancel_dias) : '',
    cancel_penalidad_noches: plan?.cancel_penalidad_noches != null ? String(plan.cancel_penalidad_noches) : '',
    noshow_noches: plan?.noshow_noches != null ? String(plan.noshow_noches) : '',
    partner_id: plan?.partner_id ?? '', es_publico: plan?.es_publico ?? true,
    orden: plan?.orden ?? 0, es_activo: plan?.es_activo ?? true,
  });
  const guardar = useMutation({
    mutationFn: () => hotelRpc('plan_guardar', {
      _d: {
        id: plan?.id, codigo: form.codigo.trim(), nombre: form.nombre.trim(),
        ajuste_tipo: form.ajuste_tipo, ajuste_valor: form.ajuste_valor,
        incluye_desayuno: form.incluye_desayuno, reembolsable: form.reembolsable,
        deposito_pct: form.deposito_pct === '' ? null : Number(form.deposito_pct),
        cancel_dias: form.cancel_dias === '' ? null : Number(form.cancel_dias),
        cancel_penalidad_noches: form.cancel_penalidad_noches === '' ? null : Number(form.cancel_penalidad_noches),
        noshow_noches: form.noshow_noches === '' ? null : Number(form.noshow_noches),
        partner_id: form.partner_id || null, es_publico: form.es_publico, orden: form.orden, es_activo: form.es_activo,
      },
    }),
    onSuccess: () => { toast.success('Plan de tarifa guardado'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Modal title={plan ? `Editar plan ${plan.codigo}` : 'Nuevo plan de tarifa'} onClose={onClose} wide>
      <div className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">El ajuste se aplica sobre la tarifa base del tipo de habitación. Deja vacíos los campos de política para que hereden la configuración general del hotel.</p>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Código *"><input className={fieldCls} value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })} /></Campo>
          <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Campo label="Tipo de ajuste">
            <select className={fieldCls} value={form.ajuste_tipo} onChange={(e) => setForm({ ...form, ajuste_tipo: e.target.value as 'pct' | 'monto' })}>
              <option value="pct">Porcentaje (%)</option>
              <option value="monto">Monto fijo ($)</option>
            </select>
          </Campo>
          <Campo label={`Valor (${form.ajuste_tipo === 'pct' ? '%, negativo = descuento' : '$, negativo = descuento'})`}>
            <input type="number" step="0.01" className={fieldCls} value={form.ajuste_valor} onChange={(e) => setForm({ ...form, ajuste_valor: Number(e.target.value) || 0 })} />
          </Campo>
          <Campo label="Orden"><input type="number" className={fieldCls} value={form.orden} onChange={(e) => setForm({ ...form, orden: Number(e.target.value) || 0 })} /></Campo>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Switch checked={form.incluye_desayuno} onChange={(v) => setForm({ ...form, incluye_desayuno: v })} label="Incluye desayuno" />
          <Switch checked={form.reembolsable} onChange={(v) => setForm({ ...form, reembolsable: v })} label="Reembolsable" />
          <Switch checked={form.es_publico} onChange={(v) => setForm({ ...form, es_publico: v })} label="Visible al público (motor de reservas)" />
          <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
        </div>
        <p className="text-xs font-semibold uppercase text-muted-foreground border-t pt-2">Política propia (vacío = hereda la config. general)</p>
        <div className="grid grid-cols-4 gap-3">
          <Campo label="Depósito %"><input type="number" min={0} max={100} className={fieldCls} placeholder="Hereda" value={form.deposito_pct} onChange={(e) => setForm({ ...form, deposito_pct: e.target.value })} /></Campo>
          <Campo label="Cancelar hasta (días)"><input type="number" min={0} className={fieldCls} placeholder="Hereda" value={form.cancel_dias} onChange={(e) => setForm({ ...form, cancel_dias: e.target.value })} /></Campo>
          <Campo label="Penalidad (noches)"><input type="number" min={0} className={fieldCls} placeholder="Hereda" value={form.cancel_penalidad_noches} onChange={(e) => setForm({ ...form, cancel_penalidad_noches: e.target.value })} /></Campo>
          <Campo label="No-show (noches)"><input type="number" min={0} className={fieldCls} placeholder="Hereda" value={form.noshow_noches} onChange={(e) => setForm({ ...form, noshow_noches: e.target.value })} /></Campo>
        </div>
        <Campo label="Tarifa corporativa de (opcional)">
          <select className={fieldCls} value={form.partner_id} onChange={(e) => setForm({ ...form, partner_id: e.target.value })}>
            <option value="">— Plan general (no ligado a un cliente) —</option>
            {partners.map((p) => <option key={p.id} value={p.id}>{p.razon_social}{p.identificacion ? ` · ${p.identificacion}` : ''}</option>)}
          </select>
        </Campo>
        {plan ? <ComponentesPlan planId={plan.id} /> : <p className="text-xs text-muted-foreground border-t pt-2">Guarde el plan para agregarle lo que incluye (desayuno, tours, spa…).</p>}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} disabled={!form.codigo.trim() || !form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Lo que incluye un plan (script 934): cada componente se cobra aparte, con su
// IVA y su ingreso; precio sin IVA por unidad.
interface Componente { id: string; plan_id: string; nombre: string; tipo: 'ayb' | 'paquete'; precio: number; base_calculo: string; iva_pct: number | null; orden: number; es_activo: boolean }
const BASES: Record<string, string> = { noche_persona: 'por noche y persona', noche: 'por noche', estadia_persona: 'una vez por persona', estadia: 'una vez por estadía' };

function ComponentesPlan({ planId }: { planId: string }) {
  const qc = useQueryClient();
  const { data: comps = [] } = useQuery<Componente[]>({
    queryKey: [QK, 'plan_componentes', planId],
    queryFn: () => hotelTabla<Componente>('plan_componentes', `plan_id=eq.${planId}&order=orden`),
  });
  const vacio = { nombre: '', tipo: 'ayb' as Componente['tipo'], precio: 0, base_calculo: 'noche_persona', iva_pct: '' };
  const [n, setN] = useState(vacio);
  const refrescar = () => void qc.invalidateQueries({ queryKey: [QK] });
  const guardar = useMutation({
    mutationFn: () => hotelRpc('plan_componente_guardar', { _d: { plan_id: planId, nombre: n.nombre.trim(), tipo: n.tipo, precio: n.precio,
      base_calculo: n.base_calculo, iva_pct: n.iva_pct === '' ? null : Number(n.iva_pct), orden: comps.length + 1 } }),
    onSuccess: () => { toast.success('Componente agregado'); setN(vacio); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => hotelRpc('plan_componente_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Componente eliminado'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <div className="space-y-2 border-t pt-2">
      <p className="text-xs font-semibold uppercase text-muted-foreground">Incluye (se cobra aparte de la habitación)</p>
      {comps.length === 0 && <p className="text-xs text-muted-foreground">Sin componentes: el plan solo ajusta la tarifa de la habitación.</p>}
      {comps.map((c) => (
        <div key={c.id} className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-xs">
          <span><strong>{c.nombre}</strong> · {money(c.precio)} {BASES[c.base_calculo] ?? c.base_calculo} · {c.tipo === 'ayb' ? 'Alimentos y bebidas' : 'Tours / spa / otros'}{c.iva_pct != null ? ` · IVA ${c.iva_pct}%` : ''}</span>
          <button type="button" className="text-red-600 hover:underline" onClick={() => eliminar.mutate(c.id)}>Quitar</button>
        </div>
      ))}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
        <Campo label="Nombre" className="col-span-2"><input className={fieldCls} value={n.nombre} placeholder="Ej.: Desayuno buffet" onChange={(e) => setN({ ...n, nombre: e.target.value })} /></Campo>
        <Campo label="Tipo">
          <select className={fieldCls} value={n.tipo} onChange={(e) => setN({ ...n, tipo: e.target.value as Componente['tipo'] })}>
            <option value="ayb">Alimentos y bebidas</option><option value="paquete">Tour / spa / otro</option>
          </select>
        </Campo>
        <Campo label="Precio sin IVA"><input type="number" min={0} step="0.01" className={fieldCls} value={n.precio} onChange={(e) => setN({ ...n, precio: Number(e.target.value) || 0 })} /></Campo>
        <Campo label="Se cobra">
          <select className={fieldCls} value={n.base_calculo} onChange={(e) => setN({ ...n, base_calculo: e.target.value })}>
            {Object.entries(BASES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Campo>
      </div>
      <div className="flex items-end justify-between gap-2">
        <Campo label="IVA % (vacío = tarifa general)" className="w-48"><input type="number" min={0} className={fieldCls} value={n.iva_pct} onChange={(e) => setN({ ...n, iva_pct: e.target.value })} /></Campo>
        <Button size="sm" variant="outline" isLoading={guardar.isPending} disabled={!n.nombre.trim() || n.precio <= 0} onClick={() => guardar.mutate()}><Plus className="mr-1 h-3.5 w-3.5" /> Agregar</Button>
      </div>
    </div>
  );
}
